import { EventEmitter, Injectable, Output, Directive } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ScheduleHotKeyService } from "../hot-key/schedule.hot-key.service";

@Directive()
@Injectable()
export class ScheduleService {
    @Output() scheduleSource: EventEmitter<any> = new EventEmitter();

    static readonly statusColorsStorageKey = "schedule_status_colors";

    static readonly defaultStatusColors = {
        scheduleStatus: {
            B: { primary: "#F44336", secondary: "#FFCDD2" },
            X: { primary: "#FF9800", secondary: "#FFE0B2" },
            L: { primary: "#1e90ff", secondary: "#D1E8FF" },
            V: { primary: "#FF9800", secondary: "#FFE0B2" },
            R: { primary: "#FF9800", secondary: "#FFE0B2" },
            U: { primary: "#FF9800", secondary: "#FFE0B2" },
            A: { primary: "#FF9800", secondary: "#FFE0B2" },
            S: { primary: "#FF9800", secondary: "#FFE0B2" },
        },
        patientStatus: {
            T: { primary: "#EF9A9A", secondary: "#EF9A9A" },
            R: { primary: "#FFCDD2", secondary: "#FFCDD2" },
            E: { primary: "#C8E6C9", secondary: "#C8E6C9" },
            X: { primary: "#FFEE58", secondary: "#FFEE58" },
            L: { primary: "#CE93D8", secondary: "#CE93D8" },
            P: { primary: "#E1BEE7", secondary: "#E1BEE7" },
            U: { primary: "#E57373", secondary: "#E57373" },
            A: { primary: "#006064", secondary: "#B2EBF2" },
            C: { primary: "#FFE0B2", secondary: "#FFE0B2" },
            Z: { primary: "#B0BEC5", secondary: "#B0BEC5" },
            V: { primary: "#FF9800", secondary: "#FFE0B2" },
            W: { primary: "#FFAB91", secondary: "#FFAB91" },
            Q: { primary: "#E0E0E0", secondary: "#E0E0E0" },
            F: { primary: "#81C784", secondary: "#81C784" },
            B: { primary: "#F44336", secondary: "#FFCDD2" },
            I: { primary: "#E0E0E0", secondary: "#E0E0E0" },
            S: { primary: "#90A4AE", secondary: "#90A4AE" },
            N: { primary: "", secondary: "" },
        },
    };

    private static statusColors = ScheduleService.cloneStatusColors(ScheduleService.defaultStatusColors);
    private static patientStatusStyleCache: { [key: string]: any } = {};
    private static emptyPatientStatusStyle: any = {};
    private static statusColorsLoaded = false;
    private static statusColorsLoadingPromise: Promise<void> | null = null;

    static scheduleStatus = [
        { status: "B", icon: "lock", name: "Bloqueado" },
        { status: "X", icon: "group_add", name: "Extra" },
        { status: "L", icon: "radio_button_unchecked", name: "Livre" },
        { status: "V", icon: "filter_1", name: "1º Vez" },
        { status: "R", icon: "description", name: "Resultado" },
        { status: "U", icon: "local_hospital", name: "Urgente" },
        { status: "A", icon: "how_to_reg", name: "Agendado" },
        { status: "S", icon: "computer", name: "Agendado pelo Portal" },
    ];

    static patientStatus = [
        { status: "T", name: "Atrasado", icon: "hourglass_empty" },
        { status: "R", name: "Paciente pronto", icon: "accessibility_new" },
        { status: "E", name: "Em atendimento", icon: "edit_location" },
        { status: "X", name: "Médico atendeu", icon: "offline_pin" },
        { status: "L", name: "Médico faltou", icon: "work_off" },
        { status: "P", name: "Presente", icon: "where_to_vote" },
        { status: "U", name: "Urgente", icon: "local_hospital" },
        { status: "A", name: "Atendido", icon: "beenhere" },
        { status: "C", name: "Confirmado", icon: "done" },
        { status: "Z", name: "Faltou e Não Avisou", icon: "backspace" },
        { status: "V", name: "1º Vez", icon: "filter_1" },
        { status: "W", name: "Desistiu", icon: "not_interested" },
        { status: "Q", name: "Não confirmado", icon: "announcement" },
        { status: "F", name: "Faltou", icon: "call_missed_outgoing" },
        { status: "B", name: "Cancelado", icon: "report_off" },
        { status: "I", name: "Paciente Sem Cadastro", icon: "new_releases" },
        { status: "S", name: "Remarcou", icon: "update" },
    ];

    constructor() { }

    public static loadStatusColorsFromStorage() {
        ScheduleService.loadStatusColors(
            localStorage.getItem(ScheduleService.statusColorsStorageKey),
        );
    }

    public static loadStatusColorsFromBackend(scheduleProvider: any, forceReload = false): Promise<void> {
        if (ScheduleService.statusColorsLoaded && !forceReload) {
            return Promise.resolve();
        }

        if (ScheduleService.statusColorsLoadingPromise && !forceReload) {
            return ScheduleService.statusColorsLoadingPromise;
        }

        if (!ScheduleService.statusColorsLoadingPromise || forceReload) {
            ScheduleService.loadStatusColorsFromStorage();
        }

        if (!scheduleProvider || !scheduleProvider.getStatusColors) {
            ScheduleService.statusColorsLoaded = true;
            return Promise.resolve();
        }

        const loadingPromise = scheduleProvider.getStatusColors().then((response) => {
            ScheduleService.loadStatusColorsFromEartefact(response ? response.body : null);
            ScheduleService.statusColorsLoaded = true;
        }).catch(() => {
            ScheduleService.statusColorsLoaded = true;
        }).then(() => {
            ScheduleService.statusColorsLoadingPromise = null;
        });

        ScheduleService.statusColorsLoadingPromise = loadingPromise;

        return loadingPromise;
    }

    public static loadStatusColorsFromEartefact(eartefact: any) {
        const content = eartefact && eartefact.content ? eartefact.content : null;
        ScheduleService.loadStatusColors(content);
        ScheduleService.saveStatusColorsToStorage(ScheduleService.statusColors);
        ScheduleService.statusColorsLoaded = true;
    }

    public static saveStatusColorsToStorage(value: any) {
        try {
            localStorage.setItem(
                ScheduleService.statusColorsStorageKey,
                JSON.stringify(ScheduleService.normalizeStatusColors(value)),
            );
        } catch (error) {
        }
    }

    public static loadStatusColors(value: any) {
        ScheduleService.statusColors = ScheduleService.normalizeStatusColors(value);
        ScheduleService.rebuildPatientStatusStyleCache();
    }

    public static updateStatusColorsCache(value: any) {
        ScheduleService.loadStatusColors(value);
        ScheduleService.saveStatusColorsToStorage(ScheduleService.statusColors);
        ScheduleService.statusColorsLoaded = true;
        ScheduleService.statusColorsLoadingPromise = null;
    }

    public static getDefaultStatusColors() {
        return ScheduleService.cloneStatusColors(ScheduleService.defaultStatusColors);
    }

    public static getStatusColors() {
        return ScheduleService.cloneStatusColors(ScheduleService.statusColors);
    }

    public static getDefaultStatusColorsValue() {
        return JSON.stringify(ScheduleService.getDefaultStatusColors(), null, 2);
    }

    public static normalizeStatusColors(value: any) {
        let colors = ScheduleService.cloneStatusColors(ScheduleService.defaultStatusColors);
        let parsedValue = value;

        if (typeof parsedValue === "string") {
            if (!parsedValue.trim()) {
                return colors;
            }

            try {
                parsedValue = JSON.parse(parsedValue);
            } catch (error) {
                return colors;
            }
        }

        if (!parsedValue || typeof parsedValue !== "object") {
            return colors;
        }

        ["scheduleStatus", "patientStatus"].forEach((group) => {
            if (!parsedValue[group] || typeof parsedValue[group] !== "object") {
                return;
            }

            Object.keys(colors[group]).forEach((status) => {
                const customColor = parsedValue[group][status];
                if (!customColor || typeof customColor !== "object") {
                    return;
                }

                colors[group][status] = {
                    primary: ScheduleService.normalizeColor(
                        customColor.primary,
                        colors[group][status].primary,
                    ),
                    secondary: ScheduleService.normalizeColor(
                        customColor.secondary,
                        colors[group][status].secondary,
                    ),
                };
            });
        });

        return colors;
    }

    public static scheduleStatusColor(status: string) {
        return (
            ScheduleService.statusColors.scheduleStatus[status] ||
            ScheduleService.statusColors.scheduleStatus.A
        );
    }

    public static patientStatusColor(status: string) {
        return (
            ScheduleService.statusColors.patientStatus[status] ||
            ScheduleService.statusColors.patientStatus.N
        );
    }

    public static patientStatusStyle(status: string) {
        if (!Object.keys(ScheduleService.patientStatusStyleCache).length) {
            ScheduleService.rebuildPatientStatusStyleCache();
        }

        return (
            ScheduleService.patientStatusStyleCache[status] ||
            ScheduleService.patientStatusStyleCache["N"] ||
            ScheduleService.emptyPatientStatusStyle
        );
    }

    public static patientStatusBackgroundColor(status: string) {
        const color = ScheduleService.patientStatusColor(status);

        if (!color || !color.secondary) {
            return "";
        }

        return color.secondary;
    }

    public static scheduleStatusEventColor(schedule: any) {
        if (!schedule || (!schedule.patient && schedule.scheduleStatus == "L")) {
            return ScheduleService.scheduleStatusColor("L");
        }

        if (schedule.scheduleStatus == "B") {
            return ScheduleService.scheduleStatusColor("B");
        }

        return ScheduleService.scheduleStatusColor(schedule.scheduleStatus || "A");
    }

    private static cloneStatusColors(value: any) {
        return JSON.parse(JSON.stringify(value));
    }

    private static normalizeColor(value: string, fallback: string) {
        if (!value) {
            return fallback;
        }

        const color = value.toString().trim();
        const isHex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(color);

        return isHex ? color : fallback;
    }

    private static rebuildPatientStatusStyleCache() {
        const styleCache: { [key: string]: any } = {};

        Object.keys(ScheduleService.statusColors.patientStatus).forEach((status) => {
            const backgroundColor = ScheduleService.patientStatusBackgroundColor(status);
            styleCache[status] = backgroundColor
                ? { "background-color": backgroundColor }
                : ScheduleService.emptyPatientStatusStyle;
        });

        ScheduleService.patientStatusStyleCache = styleCache;
    }



    public static scheduleStatusIcon(status) {
        const icon = this.scheduleStatus.find((el) => el.status === status);
        return icon ? icon.icon : "lens";
    }

    public static scheduleStatusTitle(status: string) {

        const icon = this.scheduleStatus.find((el) => el.status === status);
        return icon ? icon.name : "lens";
    }

    public static getpatientStatusTitle(status: string) {

        const icon = this.patientStatus.find((el) => el.status === status);
        return icon ? icon.name : "";
    }



    public static patientStatusIcon(status) {
        const icon = this.patientStatus.find((el) => el.status === status);
        return icon ? icon.icon : "lens";
    }

    public static setField(scheduleForm, schedule): FormGroup {
        scheduleForm.controls["id"].setValue(schedule.id);
        scheduleForm.controls["performer"].setValue(schedule.performer, {
            eventEmmiter: false,
        });
        scheduleForm.controls["insurance"].setValue(schedule.insurance, {
            eventEmmiter: false,
        });
        scheduleForm.controls["procedure"].setValue(schedule.procedure, {
            eventEmmiter: false,
        });
        scheduleForm.controls["horary"].setValue(schedule.horary);
        scheduleForm.controls["company"].setValue(schedule.company);

        scheduleForm.controls["date"].setValue(schedule.date);
        scheduleForm.controls["dateStatus"].setValue(schedule.dateStatus);
        scheduleForm.controls["hour"].setValue(schedule.hour);
        scheduleForm.controls["hourStatus"].setValue(schedule.hourStatus);
        scheduleForm.controls["patientStatus"].setValue(schedule.patientStatus);
        scheduleForm.controls["scheduleStatus"].setValue(
            schedule.scheduleStatus
        );
        scheduleForm.controls["priority"].setValue(schedule.priority);
        scheduleForm.controls["counter"].setValue(0);
        scheduleForm.controls["operation"].setValue(schedule.operation);
        schedule.session
            ? scheduleForm.controls["session"].setValue(schedule.session)
            : scheduleForm.controls["session"].setValue({
                id: null,
                company: null,
                dateat: null,
                frequency: null,
                weekdays: null,
                quantity: 0,
                weekdayshorary: null,
                blocked: false,
                schedulebysession: null,
                orderbysession: null,
            });

        schedule.scheduleNote
            ? scheduleForm.controls["scheduleNote"].setValue(
                schedule.scheduleNote
            )
            : scheduleForm.controls["scheduleNote"].setValue({
                id: null,
                note: null,
            });

        if (schedule.patient) {
            scheduleForm.controls["patient"].setValue(schedule.patient);
        } else if (schedule.person) {
            scheduleForm.controls["patient"].setValue(schedule.person.name);
        } else {
            scheduleForm.controls["patient"].setValue(null);
        }
        return scheduleForm;
    }

    public static checkChanges(schedule, scheduleChanged) {
        // schedule.patientPersonName = scheduleChanged.patientPersonName
        if (scheduleChanged.patient) {
            schedule.patientPersonName = scheduleChanged.patient.person.name;
        }
        /* schedule.scheduleStatus = scheduleChanged.scheduleStatus;
        schedule.patientStatus = scheduleChanged.patientStatus;
        schedule.procedure = scheduleChanged.procedure;
        schedule.insurance = scheduleChanged.insurance;
        schedule.userScheduleTO = null;
        schedule.priority = null;
        schedule.priorityInput = null;
        if (scheduleChanged.priority != null && scheduleChanged.priority != '') {
            schedule.priority = scheduleChanged.priority
        } else {
            schedule.priorityInput = scheduleChanged.priorityInput;
        }

        schedule.scheduleNote = scheduleChanged.scheduleNote;


       
        if (schedule.person != scheduleChanged.person) {
            schedule.person = scheduleChanged.person;
            // schedule.procedure = scheduleChanged.procedure;
            // schedule.insurance = scheduleChanged.insurance;
        }
        // else
        //     if (schedule.markdown && !schedule.patient && !schedule.person) {
        //         schedule.patient = scheduleChanged.patient;
        //         schedule.person = scheduleChanged.person;
        //         schedule.procedure = scheduleChanged.procedure;
        //         schedule.insurance = scheduleChanged.insurance;
        //     }
        //     else {
        //         if (scheduleChanged.patient && !scheduleChanged.person) {
        //             schedule.patient = scheduleChanged.patient;
        //             schedule.procedure = scheduleChanged.procedure;
        //             schedule.insurance = scheduleChanged.insurance;
        //         }
        //         else if (scheduleChanged.person) {
        //             schedule.person = scheduleChanged.person;
        //             schedule.procedure = scheduleChanged.procedure;
        //             schedule.insurance = scheduleChanged.insurance;
        //         }
        //     }*/
        return schedule;
    }

    public setPatientSelect(set) {
        this.scheduleSource.emit(set);
    }
}
